'use client';

import { useMergeRefs } from '@floating-ui/react';
import { IconMoreFilled } from '@zillow/constellation-icons/react/filled/icon-more-filled';
import { type ComponentProps, forwardRef, useContext } from 'react';

import { cx } from '@/styled-system/css';

import { IconButton } from '../icon-button';
import { Slot } from '../slot';
import { DropdownRootContext } from './context/dropdown-root.context';
import type { DropdownTriggerPropsInterface } from './types/dropdown-trigger.types';

/**
 * @since 10.0.0
 */
export const DropdownTrigger = forwardRef<HTMLButtonElement, DropdownTriggerPropsInterface>(
  (props: DropdownTriggerPropsInterface, forwardedRef) => {
    const {
      children = <IconButton icon={<IconMoreFilled />} emphasis="bare" tone="brand" title="More" />,
      ...rest
    } = props;
    const { dropdown } = useContext(DropdownRootContext);
    const mergedRefs = useMergeRefs([dropdown.refs.setReference, forwardedRef]);
    const mergedReferenceProps = dropdown.getReferenceProps(rest) as ComponentProps<'button'>;
    const { 'aria-haspopup': _ariaHasPopup, ...referenceProps } = mergedReferenceProps;

    /*
     * Floating UI's `useRole` only sets `aria-controls` while open. Always pin it to the
     * stable contentId so closed triggers still advertise the controlled panel.
     */
    return (
      <Slot
        ref={mergedRefs}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Dropdown.Trigger"
        {...referenceProps}
        aria-controls={dropdown.contentId}
        className={cx('c11n_dropdown__trigger', referenceProps.className)}
      >
        {children}
      </Slot>
    );
  },
);

DropdownTrigger.displayName = 'Dropdown.Trigger';
